import React, { useState, useMemo, useEffect } from 'react'

import Joi from 'joi'
import toast from 'react-hot-toast'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'

import Modal from '../../../../../../components/Modal'
import {
  StandardInput,
  PhoneNumberInput,
} from '../../../../../../components/Input'
import Toggle from '../../../../../../components/Toggle'
import { Button } from '../../../../../../components/Button'
import EmptyWrapper from '../../../../../../components/EmptyWrapper'
import CharacterReferenceDetails from '../../../../../../components/CharacterReferenceDetails'

import { setCharacterReferences } from '../../../../../../redux/feature/applicantSlice'

import apiClient from '../../../../../../helpers/apiClient'

import { getFromLocalStorage } from '../../../../../../utils/util'

const CharacterReferenceUpdate = () => {
  const dispatch = useDispatch()

  const characterReferences = useSelector(
    (state) => state.applicant.characterReferences
  )

  const [crName, setName] = useState('')
  const [crEmail, setEmail] = useState('')
  const [crPhone, setPhone] = useState('')
  const [toggle, setToggle] = useState(false)
  const [updateId, setUpdateId] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})

  const applicantCVData = getFromLocalStorage('applicant-cv-data')

  const validationSchema = useMemo(() => {
    return Joi.object({
      id: Joi.number().label('ID').required(),
      name: Joi.string().label('Name').required(),
      email: Joi.string()
        .label('Email')
        .email({ tlds: { allow: false } })
        .required(),
      phone: Joi.string()
        .label('Phone')
        .pattern(/^\+?\d{10,15}$/)
        .required()
        .messages({
          'string.pattern.base':
            'Phone number must be a valid phone number with country code.',
        }),
    })
  }, [])

  useEffect(() => {
    const fetchCharacterReferences = JSON.parse(
      applicantCVData?.character_reference || '[]'
    )

    dispatch(setCharacterReferences(fetchCharacterReferences))
  }, [])

  const handleSubmit = () => {
    const userId = getFromLocalStorage('user_id')
    const reqBody = {
      character_reference: !toggle ? JSON.stringify(characterReferences) : '[]',
    }
    apiClient
      .put(`/applicant/${userId}/update`, reqBody)
      .then((resp) => {
        localStorage.setItem(
          'applicant-cv-data',
          JSON.stringify({
            ...applicantCVData,
            character_reference: resp.data.data.character_reference,
          })
        )
        toast.success(resp.data.message)
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message)
      })
  }

  const handleModifyCharacterReference = () => {
    const data = {
      id: new Date().getTime(),
      name: crName,
      email: crEmail,
      phone: crPhone,
    }

    const result = validationSchema.validate(data, { abortEarly: false })

    if (result.error) {
      const validationErrors = result.error.details.reduce((acc, curr) => {
        acc[curr.context.key] = curr.message.replace(/"/g, '')
        return acc
      }, {})

      setFieldErrors(validationErrors)
      toast.error('Please correct the validation errors before proceeding.')

      return
    } else {

      if (!updateId) {
        const newCharacterReferences = [...characterReferences, data]
        console.log(newCharacterReferences)
        dispatch(setCharacterReferences(newCharacterReferences))
      } else {
        const updatedReferences = characterReferences.map((ref) => {
          if (ref.id === updateId) {
            return { ...ref, name: crName, email: crEmail, phone: crPhone }
          } else {
            return ref
          }
        })
        dispatch(setCharacterReferences(updatedReferences))
        setUpdateId(null)
      }
    }

    setName('')
    setEmail('')
    setPhone('')
    setFieldErrors({})
  }



  const handleEditCharacterReference = (id) => {
    setUpdateId(id)
    const referenceToEdit = characterReferences.find((ref) => ref.id === id)
    if (referenceToEdit) {
      setName(referenceToEdit.name)
      setEmail(referenceToEdit.email)
      setPhone(referenceToEdit.phone)
    }
  }

  const handleDeleteCharacterReference = (id) => {
    const updatedReferences = characterReferences.filter((ref) => ref.id !== id)
    dispatch(setCharacterReferences(updatedReferences))
  }

  let charRefListComponent = null

  if (characterReferences.length > 0 && !toggle) {
    charRefListComponent = (
      <CharacterReferenceWrapper>
        {characterReferences.map((ref) => (
          <CharacterReferenceDetails
            key={ref.id}
            id={ref.id}
            crName={ref.crName || ref.name}
            crEmail={ref.crEmail || ref.email}
            crPhone={ref.crPhone || ref.phone}
            handleEdit={handleEditCharacterReference}
            handleDelete={handleDeleteCharacterReference}
          />
        ))}
      </CharacterReferenceWrapper>
    )
  } else {
    charRefListComponent = (
      <>
        <EmptyWrapper maxHeight="200px" marginBottom="1rem" marginTop="1rem">
          You have no character references.
        </EmptyWrapper>
      </>
    )
  }

  let charRefFormComponent = null

  if (!toggle) {
    charRefFormComponent = (
      <>
        <StandardInput
          title="Name"
          type="text"
          value={crName}
          onChange={(value) => setName(value)}
          required
          wrapperClass={fieldErrors.name && 'error'}
          subText={fieldErrors.name}
        />
        <StandardInput
          title="Email"
          type="email"
          value={crEmail}
          onChange={(value) => setEmail(value)}
          required
          wrapperClass={fieldErrors.email && 'error'}
          subText={fieldErrors.email}
        />
        <PhoneNumberInput
          title="Phone Number"
          country="ph"
          value={crPhone}
          required
          wrapperClass={fieldErrors.phone && 'error'}
          onChange={(value) => setPhone(value)}
          subText={fieldErrors.phone}
        />
        <FormFooter>
          <Button
            handleClick={handleModifyCharacterReference}
            width="200px"
            height="3rem"
            radius="8px"
            btnClass="primary"
          >
            {!updateId
              ? 'Add Character Reference'
              : 'Update Character Reference'}
          </Button>
        </FormFooter>
      </>
    )
  }

  return (
    <Modal title="Character Reference" btnTitle="Save" btnFunc={handleSubmit}>
      <Toggle
        heading="I have no character references"
        toggle={toggle}
        setToggle={() => setToggle(!toggle)}
      />
      {charRefListComponent}
      {charRefFormComponent}
    </Modal>
  )
}

const FormFooter = styled.div`
  display: flex;
  margin-top: 2rem;
  justify-content: space-between;
`

const CharacterReferenceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 300;
  border-radius: 3px;
  border: 1px solid rgb(238, 238, 246);
  background-color: rgba(237, 232, 232, 0.8);
  box-sizing: border-box;
  box-shadow: rgba(178, 178, 203, 0.2) 3px 3px 7px;
  padding: 0.5rem;
`

export default CharacterReferenceUpdate
